import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";

import "./numeric-attribute.css";

//Import Store
import store from '../../../../../store';
import { changeNumericValue } from "../../../../../action/products";
import { findNumericValues, getAttributeItemsKey, getAttributeKey } from "../../../../../utils/numericAttributes";

// Constants
const COLORED_PRODUCTS_CATEGORY = "produkty-barwione";
const COLOR_PLASTER_VALUE = "kolor-tynki";

// Utility functions

const validateNumericValue = (value, min, max) => {
    const numValue = Number(value);
    if (numValue < min) return min;
    if (numValue > max) return max;
    return numValue;
};

export const ProductAttribute = ({
    dataProduct,
    attributeIndex,
    attr,
    category_slug,
    setColorCode
}) => {
    let params = useParams();
    const [value, setValue] = useState('');
    const { product, attributes_items, numeric_values } = dataProduct || {};
    const attribute = product?.[getAttributeKey(attributeIndex)] ?? null;
    const { product_slug } = params;

    const isNumeric = attribute?.attribute_type === 'numeric';
    const attributeItems = attributes_items?.[getAttributeItemsKey(attributeIndex)]?.items ?? {};
    const attributeItemsArray = Object.values(attributeItems);

    const shouldDisplayColorInput =
        category_slug === COLORED_PRODUCTS_CATEGORY &&
        attr?.attribute_item_value === COLOR_PLASTER_VALUE;

    // Numeric attribute configuration
    const numericConfig = findNumericValues(numeric_values, attribute?.attribute_id);
    const {
        current_value,
        override_max_value,
        override_min_value,
        attribute: numericAttribute,
        override_default_value,
    } = numericConfig;
    const unit = numericAttribute?.numeric_config?.unit;

    // Event handlers
    const handleNumericChange = (e) => {
        // setValue(e.target.value === '' ? override_min_value : e.target.value);

        setValue(e.target.value);

        if (e.target.value === '') {
            return;
        }

        const validatedValue = validateNumericValue(
            e.target.value,
            override_min_value,
            override_max_value
        );
        store.dispatch(changeNumericValue({
            ...dataProduct,
            numeric_values: numeric_values.map(nv => {
                if (nv.attribute_id === numericAttribute.attribute_id) {
                    return { ...nv, current_value: validatedValue };
                }
                return nv;
            })
        }));
    };

    const handleBlur = () => {
        // If empty, set to minimum value
        const validatedValue = validateNumericValue(
            value,
            override_min_value,
            override_max_value
        );
        setValue(validatedValue);
        store.dispatch(changeNumericValue({
            ...dataProduct,
            numeric_values: numeric_values.map(nv => {
                if (nv.attribute_id === numericAttribute.attribute_id) {
                    return { ...nv, current_value: validatedValue };
                }
                return nv;
            })
        }));
    };

    useEffect(() => {
        // added current_value === override_default_value so on return don't set it until store get fresh value
        // temporary
        if (isNumeric && numericConfig && !value && current_value === override_default_value) {
            setValue(current_value || 0);
        }
    }, [current_value]);

    useEffect(() => {
        if (value && value !== override_default_value) {
            setValue(override_default_value);
        }
    }, [product_slug]);

    // Early returns AFTER all hooks
    if (!dataProduct?.product || !attribute) return null;

    return (
        <div className="product-filters-container">
            <div className="mt-2">
                <AttributeLabel
                    attribute={attribute}
                    unit={unit}
                    minValue={override_min_value}
                    maxValue={override_max_value}
                />

                <div className="product-single-filter">
                    {!isNumeric ? (
                        <>
                            <ul className="config-size-list">
                                {attributeItemsArray.map(attributeItem => (
                                    <AttributeListItem
                                        key={attributeItem.attribute_item_id}
                                        attributeItem={attributeItem}
                                        productCategory={product.category?.category_slug}
                                        isSelected={attributeItem.link_selected}
                                    />
                                ))}
                            </ul>
                            {shouldDisplayColorInput && (
                                <ColorCodeInput onColorCodeChange={setColorCode} />
                            )}
                        </>
                    ) : (
                        <div className="numeric-attribute">
                            <div className="numeric-attribute__wrapper">
                                <NumericInput
                                    value={value}
                                    min={0}
                                    max={override_max_value}
                                    onChange={handleNumericChange}
                                    onBlur={handleBlur}
                                    id={`numeric-input-${numericAttribute.attribute_id}`}
                                />
                                <span className="numeric-attribute__unit">{unit}</span>
                            </div>
                            <div className="slider">
                                <div className="slider__track">
                                    <div className="slider__thumb-wrapper">
                                        <div className="slider__thumb" style={{
                                            left: `${((current_value - override_min_value) / (override_max_value - override_min_value)) * 100}%`,

                                        }}>
                                            <div className="slider__thumb-triangle" >
                                                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="12px" width="12px" xmlns="http://www.w3.org/2000/svg"><path d="M20.118 3h-16.225a2.914 2.914 0 0 0 -2.503 4.371l8.116 13.549a2.917 2.917 0 0 0 4.987 .005l8.11 -13.539a2.914 2.914 0 0 0 -2.486 -4.386z"></path></svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="slider__dots">
                                    <div className="slider__dot-with-number">
                                        <span className="slider__dot"></span>
                                        <span className="slider__dot-value">{override_min_value}</span>
                                    </div>
                                    {Array.from({ length: 13 }).map((_, index) => (
                                        <span key={index} className="slider__dot"></span>
                                    ))}
                                    <div className="slider__dot-with-number">
                                        <span className="slider__dot"></span>
                                        <span className="slider__dot-value">{override_max_value}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};


// Sub-components
const AttributeLabel = ({ attribute, unit, minValue, maxValue }) => (
    <label className="text-uppercase" htmlFor={`numeric-input-${attribute.attribute_id}`}>
        {attribute.attribute_value || attribute.attribute_name}
        {unit && ` (${minValue} - ${maxValue} ${unit})`}:
    </label>
);

const ColorCodeInput = ({ onColorCodeChange }) => (
    <div className="row">
        <div className="col-lg-12">
            <input
                type="text"
                className="form-input w-100 text-center color-code"
                placeholder="Wpisz kod koloru ..."
                onChange={(e) => onColorCodeChange(e.target.value)}
            />
        </div>
    </div>
);

const AttributeListItem = ({ attributeItem, productCategory, isSelected }) => (
    <li className={isSelected ? "active col-lg-3" : "col-lg-3"}>
        <Link to={`${process.env.PUBLIC_URL}/produkty/${productCategory}/${attributeItem.link}`}>
            {attributeItem.attribute_item_name}
        </Link>
    </li>
);

const NumericInput = ({ value, min, max, onChange, onBlur, id }) => {
    const handleInput = (e) => {
        e.target.value = e.target.value.replace(/[^0-9]/g, '');
    };

    const handleFocus = (e) => {
        e.target.select();
    };

    return (
        <input
            className="form-control numeric-input"
            type="number"
            inputMode="numeric"
            min={0}
            step="1"
            max={max}
            value={value}
            onChange={onChange}
            onInput={handleInput}
            onBlur={onBlur}
            onFocus={handleFocus}
            id={id}
        />
    );
};